import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cx } from './cx';

type Variant = 'primary' | 'ghost' | 'subtle';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

// Buttons are ink, not clay. Clay is precious -- reserve it for italic emphasis.
const variants: Record<Variant, string> = {
  primary:
    'bg-ink text-paper border border-ink hover:bg-ink-2 active:bg-ink-2 disabled:opacity-50',
  ghost:
    'bg-transparent text-ink border border-rule-strong hover:bg-paper-2 disabled:opacity-50',
  subtle:
    'bg-paper-2 text-ink border border-transparent hover:bg-paper-3 disabled:opacity-50',
};

// Use `min-h-*` (not a hard `h-*`) so a button that ever holds two lines
// grows instead of clipping its label; combined with `whitespace-nowrap`
// on the base, single-line labels keep their exact pill height while
// long labels in tight rows shrink their sibling rather than wrapping
// mid-word (the mobile "Adjust / balance" clipping bug).
const sizes: Record<Size, string> = {
  sm: 'min-h-8 py-1.5 px-3 text-sm',
  md: 'min-h-10 py-2 px-4 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'inline-flex items-center justify-center gap-2 font-medium tracking-tight',
        'whitespace-nowrap',
        'transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-ink/30',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    />
  );
});
